/**
 * Chunk完整性检测器
 * 1.2.21: 用于检测流式传输的chunk是否完整（JSON和文本）
 */

import type { ChunkValidationResult } from '../types/chat';
import { isCompleteJsonInCodeBlock } from './structuredParser';

/**
 * Chunk完整性检测器类
 * 用于检测流式传输的chunk是否完整，支持JSON和文本格式
 */
export class ChunkValidator {
  private lastCompleteState: string = '';
  private validationHistory: Array<{ content: string; isComplete: boolean }> = [];

  /**
   * 检测chunk完整性
   * @param chunk 当前chunk
   * @param accumulatedContent 累积的内容
   * @returns 验证结果
   */
  validateChunk(chunk: string, accumulatedContent: string): ChunkValidationResult {
    if (!accumulatedContent || accumulatedContent.trim().length === 0) {
      return {
        isComplete: false,
        reason: 'Empty content',
        type: 'text'
      };
    }

    const trimmed = accumulatedContent.trim();
    
    // 检测JSON完整性
    const isJSON = trimmed.startsWith('{') || trimmed.startsWith('[');
    let jsonComplete = false;
    let textComplete = false;

    if (isJSON) {
      // 使用structuredParser中的JSON完整性检测
      jsonComplete = isCompleteJsonInCodeBlock(trimmed);
      
      if (jsonComplete) {
        // JSON完整，尝试解析验证
        try {
          JSON.parse(trimmed);
          this.lastCompleteState = accumulatedContent;
          return {
            isComplete: true,
            reason: 'Valid JSON',
            type: 'json'
          };
        } catch (e) {
          // JSON结构完整但解析失败，可能包含控制字符等
          return {
            isComplete: false,
            reason: `JSON parse error: ${e instanceof Error ? e.message : 'Unknown error'}`,
            type: 'json'
          };
        }
      } else {
        // JSON不完整
        return {
          isComplete: false,
          reason: 'Incomplete JSON structure',
          type: 'json'
        };
      }
    } else {
      // 文本完整性检测
      textComplete = this.validateTextCompleteness(accumulatedContent);
      
      if (textComplete) {
        this.lastCompleteState = accumulatedContent;
        return {
          isComplete: true,
          reason: 'Complete text',
          type: 'text'
        };
      } else {
        return {
          isComplete: false,
          reason: 'Incomplete text (sentence or paragraph)',
          type: 'text'
        };
      }
    }
  }

  /**
   * 检测文本完整性
   * 通过检测句子、段落等结构来判断文本是否完整
   */
  private validateTextCompleteness(text: string): boolean {
    if (!text || text.trim().length === 0) {
      return false;
    }

    // 移除末尾空白
    const trimmed = text.trim();
    
    // 检测是否以完整的句子结尾（句号、问号、感叹号等）
    const sentenceEnders = /[。！？.!?]\s*$/;
    if (sentenceEnders.test(trimmed)) {
      return true;
    }

    // 检测是否以段落结尾（双换行）
    if (trimmed.endsWith('\n\n') || trimmed.endsWith('\r\n\r\n')) {
      return true;
    }

    // 检测是否以列表项结尾（数字. 或 - 或 *）
    const listItemEnders = /(\d+[\.、]\s*.*|[-*]\s*.*)$/;
    if (listItemEnders.test(trimmed)) {
      // 列表项可能不完整，但如果是最后一个chunk，可以认为是完整的
      return true;
    }

    // 如果文本很长但没有明显的结束标记，认为可能不完整
    // 但为了不误判，如果文本超过一定长度且没有明显的未完成标记，认为完整
    if (trimmed.length > 100 && !trimmed.endsWith('，') && !trimmed.endsWith(',')) {
      // 检查是否有明显的未完成标记（如末尾是逗号、顿号等）
      return true;
    }

    // 默认认为不完整
    return false;
  }

  /**
   * 重置验证器状态
   */
  reset(): void {
    this.lastCompleteState = '';
    this.validationHistory = [];
  }

  /**
   * 获取最后一个完整状态
   */
  getLastCompleteState(): string {
    return this.lastCompleteState;
  }
}

